/**
 * Z-Report View
 * Displays the Z-report summary after session close
 */

import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import type { ZReport } from '@/types/pos.types';
import { useTranslation } from '@kgc/i18n';

interface ZReportViewProps {
  report: ZReport;
  isOpen: boolean;
  onClose: () => void;
}

export function ZReportView({ report, isOpen, onClose }: ZReportViewProps) {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const formatPrice = (value: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    // Stub - would call API to get PDF
    alert(t('sales.zReport.downloadPdfSoon'));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 print:bg-white">
      <Card className="max-h-[90vh] w-full max-w-lg overflow-y-auto print:max-h-none print:shadow-none">
        <CardHeader className="border-b print:border-none">
          <CardTitle className="flex items-center justify-between">
            <span>{t('sales.zReport.title')}</span>
            <span className="text-sm font-normal text-gray-500 dark:text-[#ccfbf1]">
              {report.sessionNumber}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 pt-6">
          {/* Session Info */}
          <section>
            <h3 className="mb-2 text-sm font-semibold text-gray-500 dark:text-[#ccfbf1]">
              {t('sales.zReport.sessionInfo')}
            </h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>{t('sales.zReport.opened')}</span>
                <span>{formatDateTime(report.openedAt)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t('sales.zReport.closed')}</span>
                <span>{formatDateTime(report.closedAt)}</span>
              </div>
            </div>
          </section>

          {/* Cash Balance */}
          <section>
            <h3 className="mb-2 text-sm font-semibold text-gray-500 dark:text-[#ccfbf1]">
              {t('sales.zReport.cashBalance')}
            </h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>{t('sales.zReport.openingBalance')}</span>
                <span>{formatPrice(report.openingBalance)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t('sales.zReport.expectedBalance')}</span>
                <span>{formatPrice(report.expectedBalance)}</span>
              </div>
              <div className="flex justify-between font-medium">
                <span>{t('sales.zReport.closingBalance')}</span>
                <span>{formatPrice(report.closingBalance)}</span>
              </div>
              <div
                className={`flex justify-between font-bold ${
                  report.variance === 0 ? 'text-green-600' : 'text-yellow-600'
                }`}
              >
                <span>{t('sales.zReport.variance')}</span>
                <span>
                  {report.variance > 0 ? '+' : ''}
                  {formatPrice(report.variance)}
                </span>
              </div>
              {report.varianceNote && (
                <div className="mt-2 rounded bg-yellow-50 p-2 text-xs text-yellow-700">
                  <span className="font-medium">{t('sales.zReport.note')}</span>{' '}
                  {report.varianceNote}
                </div>
              )}
            </div>
          </section>

          {/* Transaction Summary */}
          <section>
            <h3 className="mb-2 text-sm font-semibold text-gray-500 dark:text-[#ccfbf1]">
              {t('sales.zReport.transactions')}
            </h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>{t('sales.zReport.totalTransactions')}</span>
                <span>{report.transactionCount}</span>
              </div>
              <div className="flex justify-between text-green-600">
                <span>{t('sales.zReport.completed')}</span>
                <span>{report.completedCount}</span>
              </div>
              <div className="flex justify-between text-red-600">
                <span>{t('sales.zReport.voided')}</span>
                <span>{report.voidedCount}</span>
              </div>
            </div>
          </section>

          {/* Payment Methods */}
          <section>
            <h3 className="mb-2 text-sm font-semibold text-gray-500 dark:text-[#ccfbf1]">
              {t('sales.zReport.paymentMethods')}
            </h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>💵 {t('sales.zReport.cash')}</span>
                <span>{formatPrice(report.cashTotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>💳 {t('sales.zReport.card')}</span>
                <span>{formatPrice(report.cardTotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>🏦 {t('sales.zReport.transfer')}</span>
                <span>{formatPrice(report.transferTotal)}</span>
              </div>
              {/* Q-T152: az utalvány- és folyószámla-sor eltávolítva — a BE Z-jelentése
                  nem ad `voucherTotal` / `creditTotal` mezőt, a feltétel így sosem
                  teljesült. Hamis nullát nem mutatunk; ha kliens-igény lesz rájuk,
                  előbb a BE-nek kell számolnia őket. */}
            </div>
          </section>

          {/* Sales Summary */}
          <section>
            <h3 className="mb-2 text-sm font-semibold text-gray-500 dark:text-[#ccfbf1]">
              {t('sales.zReport.revenue')}
            </h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>{t('sales.zReport.grossSales')}</span>
                <span>{formatPrice(report.totalSales)}</span>
              </div>
              {report.totalRefunds > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>{t('sales.zReport.refunds')}</span>
                  <span>-{formatPrice(report.totalRefunds)}</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-1 font-bold">
                <span>{t('sales.zReport.netSales')}</span>
                <span>{formatPrice(report.netSales)}</span>
              </div>
              <div className="flex justify-between text-gray-500 dark:text-[#ccfbf1]">
                <span>{t('sales.zReport.taxAmount')}</span>
                <span>{formatPrice(report.totalTax)}</span>
              </div>
            </div>
          </section>

          {/* Q-T152: a „Visszaadott" blokk eltávolítva — a BE nem ad `changeGiven`
              mezőt, így ott korábban „NaN Ft" állt. A kedvezmény-sor helyett a
              ténylegesen létező `totalRefunds` (visszatérítés) jelenik meg, és csak
              akkor, ha van értéke. */}

          {/* Actions */}
          <div className="flex gap-2 pt-4 print:hidden">
            <Button type="button" variant="outline" onClick={handlePrint} className="flex-1">
              🖨️ {t('sales.zReport.print')}
            </Button>
            <Button type="button" variant="outline" onClick={handleDownloadPdf} className="flex-1">
              📄 {t('sales.zReport.downloadPdf')}
            </Button>
            <Button
              type="button"
              onClick={onClose}
              className="flex-1 bg-primary hover:bg-primary/90"
            >
              {t('sales.zReport.close')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
